/**
 * Planner › Week ahead — plan a week's work blocks and send them to Google Calendar.
 *
 * Top to bottom, the Sunday sitting: the week's tasks by project, with an
 * estimate typed straight into each row (saved on the task as estimateHours,
 * so a task that slips arrives next week already estimated); per project, how
 * much of that its standing blocks already cover and how many extra blocks
 * would cover the rest; a capacity line for the week; then Preview and Send.
 *
 * Sending saves the extra blocks as that week's one-off blocks and asks the
 * Apps Script to run. The script reads your calendars, the Worker places the
 * blocks (lib/workBlocks.ts), the script writes them. Standing blocks — the
 * old sheet — are the folded section at the bottom.
 *
 * Row hiding and added rows are per week and kept in this browser only: they
 * shape one sitting's list, not anything another device needs.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { card, Dot } from "@/components/atoms";
import { itemProjectId } from "@/components/ItemRow";
import { useChrome } from "@/context/ChromeContext";
import { useTaskEditor } from "@/context/TaskEditorContext";
import { useToast } from "@/context/ToastContext";
import { useAllItems, useProjects, useUpdateItem } from "@/hooks/useData";
import {
  useRunBlocks, useWorkBlockConfig, useWorkBlockMutations, useWorkBlocks, useWorkBlockSettings, type RunResult,
} from "@/hooks/useWorkBlocks";
import { dueLabel, shortDate } from "@/lib/format";
import { appMode } from "@/lib/pocketbase";
import { parseDuration, toIsoLocal } from "@/lib/timeRollup";
import type { Item, Project } from "@/lib/types";
import { useIsMobile } from "@/lib/useMediaQuery";
import {
  DAY_LABEL, dayKeyOf, estimateLabel, formatTime, inWeekPlan, mondayOf, nextMonday, perWeekOf,
  planWeek, projectNeed, standingMinutesByProject, weekLabel, type ProjectNeed, type WorkBlock,
} from "@/lib/workBlocks";

import { ghost, iconBtn, linkBtn, loadPref, primary, savePref, Seg } from "./parts";
import { durationText, StandingBlocks } from "./StandingBlocks";

type Which = "this" | "next";

/** A per-week list kept in this browser: hidden rows, added rows, extra-block counts. */
function useWeekStore<T>(kind: string, weekStart: string, fallback: T): [T, (v: T) => void] {
  const key = `ph.weekplan.${kind}.${weekStart}`;
  const read = (): T => { try { return JSON.parse(localStorage.getItem(key) || "null") ?? fallback; } catch { return fallback; } };
  const [value, setValue] = useState<T>(read);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setValue(read()), [key]);
  const set = (v: T) => {
    setValue(v);
    try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* private mode */ }
  };
  return [value, set];
}

interface Group {
  projectId: string;
  project?: Project;
  items: Item[];
  need: ProjectNeed;
  /** The extra blocks this week: your number if you changed it, else the
   *  saved one-off's, else the suggestion. */
  extra: number;
  hidden: number;
}

export function WeekPlanner({ tabs }: { tabs: React.ReactNode }) {
  const mobile = useIsMobile();
  const toast = useToast();
  const navigate = useNavigate();
  const { openItem } = useTaskEditor();
  const { data: items = [] } = useAllItems();
  const { data: projects = [] } = useProjects();
  const { data: blocks = [] } = useWorkBlocks();
  const { data: stored } = useWorkBlockSettings();
  const { data: config } = useWorkBlockConfig();
  const { syncWeek } = useWorkBlockMutations();
  const runBlocks = useRunBlocks();
  const updateItem = useUpdateItem();
  const settings = stored?.settings;

  const [which, setWhich] = useState<Which>("next");
  const today = toIsoLocal(new Date());
  const weekStart = which === "next" ? nextMonday(today) : mondayOf(today);
  const [hidden, setHidden] = useWeekStore<string[]>("hidden", weekStart, []);
  const [added, setAdded] = useWeekStore<string[]>("added", weekStart, []);
  const [extraSet, setExtraSet] = useWeekStore<Record<string, number>>("extra", weekStart, {});
  const [preview, setPreview] = useState<RunResult | null>(null);
  const [standingOpen, setStandingOpen] = useState(() => loadPref<string>("ph.planner.standingOpen", "0") === "1");
  const busy = syncWeek.isPending || runBlocks.isPending;

  useEffect(() => setPreview(null), [weekStart]);

  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const blockLen = settings?.blockLenMin ?? 60;

  const groups = useMemo((): Group[] => {
    const standing = standingMinutesByProject(blocks, "project");
    const hiddenSet = new Set(hidden);
    const addedSet = new Set(added);
    const byProject = new Map<string, Item[]>();
    const hiddenCount = new Map<string, number>();
    for (const item of items) {
      if (!(inWeekPlan(item, weekStart, today) || (addedSet.has(item.id) && item.status !== "done"))) continue;
      const pid = itemProjectId(item) ?? "";
      if (hiddenSet.has(item.id)) { hiddenCount.set(pid, (hiddenCount.get(pid) ?? 0) + 1); continue; }
      byProject.set(pid, [...(byProject.get(pid) ?? []), item]);
    }
    for (const pid of hiddenCount.keys()) if (!byProject.has(pid)) byProject.set(pid, []);

    return [...byProject.entries()].map(([pid, list]) => {
      const need = projectNeed(list, standing.get(pid) ?? 0, blockLen, weekStart);
      const saved = blocks.find((b) => b.week === weekStart && (b.project ?? "") === pid);
      const extra = extraSet[pid] ?? (saved ? perWeekOf(saved) : need.suggestedBlocks);
      return { projectId: pid, project: projectById.get(pid), items: sortItems(list), need, extra, hidden: hiddenCount.get(pid) ?? 0 };
    }).sort((a, b) => (a.project?.kind === "inbox" ? 1 : 0) - (b.project?.kind === "inbox" ? 1 : 0)
      || (a.project?.name ?? "").localeCompare(b.project?.name ?? ""));
  }, [items, blocks, hidden, added, extraSet, weekStart, today, blockLen, projectById]);

  // Standing project time for projects with nothing due this week.
  const standingOnly = useMemo(() => {
    const listed = new Set(groups.map((g) => g.projectId));
    return [...standingMinutesByProject(blocks, "project").entries()]
      .filter(([pid, min]) => pid && min > 0 && !listed.has(pid))
      .map(([pid, min]) => ({ project: projectById.get(pid), min }));
  }, [groups, blocks, projectById]);

  const routineMin = sumValues(standingMinutesByProject(blocks, "routine"));
  const standingProjectMin = sumValues(standingMinutesByProject(blocks, "project"));
  const extraMin = groups.reduce((n, g) => n + (g.projectId ? g.extra * blockLen : 0), 0);
  const usedMin = routineMin + standingProjectMin + extraMin;
  const freeMin = preview && preview.weekStart === weekStart ? preview.freeMinutes : null;

  // A failed check knows nothing about the two secrets, so it must not claim
  // they are missing; the error line below says what actually went wrong.
  const needsSetup = appMode === "live" && config && !config.error && (!config.token || !config.scriptUrl);

  async function go(dryRun: boolean) {
    // The extra blocks become the week's one-offs first, so the run (or the
    // preview, which reads them from PocketBase) sees exactly this plan.
    const wanted: Omit<WorkBlock, "id">[] = groups
      .filter((g) => g.projectId && g.extra > 0)
      .map((g) => ({
        project: g.projectId, purpose: "project", title: "", durationMin: blockLen, perWeek: g.extra,
        week: weekStart, finishBy: g.need.finishBy, itemIds: g.items.map((i) => i.id), position: 0,
      }));
    try {
      await syncWeek.mutateAsync({ week: weekStart, wanted, existing: blocks });
    } catch {
      toast("Could not save this week's extra blocks");
      return;
    }

    if (appMode !== "live") {
      // The demo has no Google: place against an empty calendar so the
      // preview still shows how the rules lay the week out.
      const now = new Date();
      const plan = planWeek({
        weekStart, today, nowMin: now.getHours() * 60 + now.getMinutes(), busy: [], kept: [],
        blocks: [...blocks.filter((b) => b.week !== weekStart), ...wanted.map((w, i) => ({ ...w, id: `demo${i}` }))],
        settings, projectNames: Object.fromEntries(projects.map((p) => [p.id, p.name])),
      });
      setPreview({
        ok: true, dryRun: true, weekStart, placements: plan.placements, freeMinutes: plan.freeMinutes, errors: [],
        results: plan.results.map((r) => ({ blockId: r.blockId, title: r.title, status: r.status, missed: r.missed })),
        counts: { placed: plan.placements.length, missed: plan.results.reduce((n, r) => n + r.missed, 0), kept: 0, removed: 0 },
      });
      if (!dryRun) toast("Demo: nothing is sent to Google. This is where the blocks would go.");
      return;
    }

    try {
      const result = await runBlocks.mutateAsync({ weekStart, dryRun });
      setPreview(result);
      const { placed, missed } = result.counts;
      toast(dryRun
        ? `Preview: ${placed} block${placed === 1 ? "" : "s"} would be placed${missed ? `, ${missed} found no slot` : ""}`
        : `Sent: ${placed} block${placed === 1 ? "" : "s"} on your calendar${missed ? `, ${missed} found no slot` : ""}`);
    } catch (err) {
      toast(err instanceof Error ? err.message : "The run failed");
    }
  }

  function setExtra(pid: string, n: number) {
    setExtraSet({ ...extraSet, [pid]: Math.max(0, Math.min(20, n)) });
  }

  function saveEstimate(item: Item, text: string) {
    const min = text.trim() ? parseDuration(text) : 0;
    if (min == null) { toast("Type the estimate as h:mm, like 0:45 or 1:30. A plain number is minutes."); return; }
    if (Math.round((item.estimateHours ?? 0) * 60) === min) return;
    const h = min / 60;
    // null, not undefined: undefined is dropped from the request and the old
    // estimate would silently stay.
    updateItem.mutate({ id: item.id, estimateHours: h > 0 ? h : (null as unknown as number) }, {
      onError: () => toast("Could not save that estimate"),
    });
  }

  useChrome({ title: "Planner", subtitle: "Work blocks for the week, onto Google Calendar", tabs }, []);

  return (
    <div style={{ padding: mobile ? "16px 16px 90px" : "20px 36px 90px", display: "flex", flexDirection: "column", gap: 16, maxWidth: 1100 }}>
      {needsSetup && <SetupNote token={config.token} scriptUrl={config.scriptUrl} />}
      {appMode === "live" && config?.error && (
        <div style={{ ...card, padding: "12px 16px", fontSize: 12.5, color: "var(--warn)" }}>Could not check the work-block setup: {config.error}</div>
      )}

      {/* -- Week and actions --------------------------------------------------- */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Seg value={which} onChange={setWhich} options={[["this", "Rest of this week"], ["next", "Next week"]]} />
        <span className="mono" style={{ fontSize: 13, color: "var(--ink2)" }}>{weekLabel(weekStart)}</span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {busy && <span style={{ fontSize: 12, color: "var(--muted)" }}>Checking your calendars…</span>}
          <button onClick={() => void go(true)} disabled={busy || !!needsSetup} style={ghost}>Preview</button>
          <button onClick={() => void go(false)} disabled={busy || !!needsSetup} style={primary}>Send to Google</button>
        </span>
      </div>
      <LastRun lastRun={stored?.lastRun ?? null} />

      {/* -- Capacity ----------------------------------------------------------- */}
      <div style={{ ...card, padding: "14px 16px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12 }}>
        <Figure label="Free time" value={freeMin == null ? "—" : durationText(freeMin)}
          hint={freeMin == null ? "Preview to read your calendars" : "Work day, minus meetings"} />
        <Figure label="Routine" value={durationText(routineMin)} hint="Standing routine blocks" />
        <Figure label="Project work" value={durationText(standingProjectMin + extraMin)}
          hint={`${durationText(standingProjectMin)} standing + ${durationText(extraMin)} extra`} />
        <Figure label="Spare" value={freeMin == null ? "—" : `${freeMin - usedMin >= 0 ? "" : "−"}${durationText(Math.abs(freeMin - usedMin))}`}
          tone={freeMin == null ? undefined : freeMin - usedMin < 0 ? "var(--bad)" : "var(--goodInk)"}
          hint={freeMin != null && freeMin - usedMin < 0 ? "Overbooked: trim estimates or blocks" : "Free time not yet blocked"} />
      </div>

      {preview && preview.weekStart === weekStart && <PreviewCard result={preview} />}

      {/* -- Tasks by project --------------------------------------------------- */}
      <div>
        <div style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)", marginBottom: 4 }}>Tasks by project</div>
        <div style={{ fontSize: 12.5, color: "var(--muted)", marginBottom: 10 }}>
          Scheduled or due {which === "next" ? "next week" : "this week"}, plus anything late. Type an estimate on each row as h:mm (a plain number is minutes); it is saved on the task.
        </div>
        {groups.length === 0 && (
          <div style={{ ...card, padding: "22px 16px", fontSize: 13, color: "var(--muted)" }}>Nothing scheduled or due {which === "next" ? "next week" : "for the rest of this week"}.</div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {groups.map((g) => (
            <ProjectCard
              key={g.projectId || "none"}
              group={g}
              blockLen={blockLen}
              allItems={items}
              onOpenProject={g.project && g.project.kind !== "inbox" ? () => navigate(`/project/${g.projectId}`) : undefined}
              onOpenItem={openItem}
              onEstimate={saveEstimate}
              onHide={(id) => setHidden([...hidden, id])}
              onShowHidden={() => setHidden(hidden.filter((id) => !items.some((i) => i.id === id && (itemProjectId(i) ?? "") === g.projectId)))}
              onAdd={(id) => { setAdded([...added, id]); setHidden(hidden.filter((h) => h !== id)); }}
              onExtra={(n) => setExtra(g.projectId, n)}
            />
          ))}
        </div>
        {standingOnly.length > 0 && (
          <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 10 }}>
            Standing project time with nothing due this week:{" "}
            {standingOnly.map((s, i) => (
              <span key={i}>{i > 0 && ", "}{s.project?.name ?? "No project"} {durationText(s.min)}</span>
            ))}
          </div>
        )}
      </div>

      {settings && (
        <StandingBlocks
          blocks={blocks}
          projects={projects}
          settings={settings}
          open={standingOpen}
          onToggle={() => { setStandingOpen(!standingOpen); savePref("ph.planner.standingOpen", standingOpen ? "0" : "1"); }}
        />
      )}
    </div>
  );
}

/* -- Pieces ----------------------------------------------------------------- */

function ProjectCard({ group: g, blockLen, allItems, onOpenProject, onOpenItem, onEstimate, onHide, onShowHidden, onAdd, onExtra }: {
  group: Group; blockLen: number; allItems: Item[];
  onOpenProject?: () => void; onOpenItem: (i: Item) => void; onEstimate: (i: Item, text: string) => void;
  onHide: (id: string) => void; onShowHidden: () => void; onAdd: (id: string) => void; onExtra: (n: number) => void;
}) {
  const { need } = g;
  const listed = new Set(g.items.map((i) => i.id));
  const addable = allItems
    .filter((i) => i.status !== "done" && i.type !== "milestone" && !listed.has(i.id) && (itemProjectId(i) ?? "") === g.projectId)
    .sort((a, b) => a.title.localeCompare(b.title))
    .slice(0, 80);
  const name = g.project ? (g.project.kind === "inbox" ? "Inbox" : g.project.name) : "No project";

  return (
    <div style={{ ...card, padding: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", flexWrap: "wrap", borderBottom: g.items.length ? "1px solid var(--line)" : "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: "1 1 260px" }}>
          {g.project && <Dot color={g.project.color} size={9} />}
          {onOpenProject
            ? <button onClick={onOpenProject} style={{ ...linkBtn, color: "var(--ink)", fontSize: 14, fontWeight: 600 }}>{name}</button>
            : <span style={{ fontSize: 14, fontWeight: 600 }}>{name}</span>}
          <span style={{ fontSize: 12, color: "var(--muted)" }}>
            Tasks {durationText(need.estimateMin)}
            {need.unestimated > 0 && <span style={{ color: "var(--warn)" }}> · {need.unestimated} without estimate</span>}
            {" "}· Standing {durationText(need.standingMin)}
            {" "}· {need.gapMin > 0 ? <strong style={{ color: "var(--ink)" }}>Need {durationText(need.gapMin)}</strong> : need.unestimated ? "Covered so far" : "Covered"}
          </span>
        </div>
        {g.projectId && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--ink3)" }}>
            Extra blocks
            <button onClick={() => onExtra(g.extra - 1)} disabled={g.extra <= 0} aria-label="One fewer" style={stepBtn}>−</button>
            <span className="mono" style={{ minWidth: 18, textAlign: "center", color: "var(--ink)", fontWeight: 600 }}>{g.extra}</span>
            <button onClick={() => onExtra(g.extra + 1)} aria-label="One more" style={stepBtn}>+</button>
            × {durationText(blockLen)}
            {g.extra !== need.suggestedBlocks && (
              <button onClick={() => onExtra(need.suggestedBlocks)} style={{ ...linkBtn, fontSize: 12 }} title="Go back to the suggested number">
                (suggested {need.suggestedBlocks})
              </button>
            )}
            {g.extra > 0 && need.finishBy && <span style={{ color: "var(--muted)" }}>by {DAY_LABEL[dayKeyOf(need.finishBy)]} {shortDate(need.finishBy)}</span>}
          </div>
        )}
      </div>

      {g.items.map((item) => <TaskRow key={item.id} item={item} onOpen={() => onOpenItem(item)} onEstimate={(t) => onEstimate(item, t)} onHide={() => onHide(item.id)} />)}

      <div style={{ display: "flex", gap: 14, alignItems: "center", padding: "8px 16px", fontSize: 12.5, flexWrap: "wrap" }}>
        {addable.length > 0 && (
          <select value="" onChange={(e) => e.target.value && onAdd(e.target.value)} aria-label="Add a task to this week"
            style={{ border: "none", background: "none", color: "var(--accent)", fontSize: 12.5, cursor: "pointer", padding: 0, maxWidth: 260 }}>
            <option value="">+ Add a task from {name}</option>
            {addable.map((i) => <option key={i.id} value={i.id}>{i.title}</option>)}
          </select>
        )}
        {g.hidden > 0 && <button onClick={onShowHidden} style={{ ...linkBtn, color: "var(--muted)" }}>Show {g.hidden} hidden</button>}
      </div>
    </div>
  );
}

function TaskRow({ item, onOpen, onEstimate, onHide }: { item: Item; onOpen: () => void; onEstimate: (text: string) => void; onHide: () => void }) {
  const [text, setText] = useState(estimateLabel(item.estimateHours));
  useEffect(() => setText(estimateLabel(item.estimateHours)), [item.estimateHours]);
  const due = dueLabel(item.due, "Due ", item.status);
  const scheduled = item.start && item.start !== item.due ? dueLabel(item.start, "", item.status) : null;
  const missing = !(Number(item.estimateHours) > 0);

  return (
    <div className="row-hover" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto 76px 24px", gap: 10, alignItems: "center", padding: "7px 16px", borderBottom: "1px solid var(--tint2)" }}>
      <button onClick={onOpen} style={{ border: "none", background: "none", padding: 0, textAlign: "left", cursor: "pointer", color: "var(--ink)", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {item.title}
      </button>
      <span style={{ fontSize: 11.5, whiteSpace: "nowrap", color: due.overdue ? "var(--bad)" : "var(--muted)" }}>
        {scheduled && <span style={{ color: "var(--muted)" }}>{scheduled.text} · </span>}{due.text}
      </span>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => onEstimate(text)}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
        placeholder="h:mm"
        aria-label={`Estimate for ${item.title}`}
        className="mono"
        style={{
          width: "100%", boxSizing: "border-box", padding: "5px 8px", borderRadius: 7, fontSize: 12.5, textAlign: "right",
          border: `1px solid ${missing ? "var(--warn)" : "var(--line2)"}`, background: "var(--surface)", color: "var(--ink)",
        }}
      />
      <button onClick={onHide} aria-label={`Leave ${item.title} out of this week`} title="Leave out of this week" style={iconBtn}>✕</button>
    </div>
  );
}

function PreviewCard({ result }: { result: RunResult }) {
  const byDay = new Map<string, RunResult["placements"]>();
  for (const p of result.placements) byDay.set(p.date, [...(byDay.get(p.date) ?? []), p]);
  const misses = result.results.filter((r) => r.missed > 0);
  const { placed, kept, removed } = result.counts;

  return (
    <div style={{ ...card, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)" }}>{result.dryRun ? "Preview" : "Sent"}</span>
        <span style={{ fontSize: 12.5, color: "var(--muted)" }}>
          {placed} placed{kept ? ` · ${kept} kept where you put them` : ""}{removed ? ` · ${removed} old blocks cleared` : ""}
          {result.dryRun ? " · nothing written yet" : ""}
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 12 }}>
        {[...byDay.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, list]) => (
          <div key={date}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--muted2)", marginBottom: 4 }}>
              {DAY_LABEL[dayKeyOf(date)]} {shortDate(date)}
            </div>
            {list.sort((a, b) => a.start - b.start).map((p, i) => (
              <div key={i} style={{ fontSize: 12.5, color: "var(--ink2)", padding: "2px 0" }}>
                <span className="mono" style={{ color: "var(--muted)" }}>{formatTime(p.start)}</span> {p.title}
              </div>
            ))}
          </div>
        ))}
      </div>
      {misses.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--warn)" }}>
          No slot: {misses.map((m) => `${m.title} (${m.missed})`).join(", ")}. Widen that block's window or days, or free some time.
        </div>
      )}
      {result.errors.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: "var(--bad)" }}>{result.errors.join(" · ")}</div>
      )}
    </div>
  );
}

function LastRun({ lastRun }: { lastRun: { at: string; source: string; placed: number; missed: number; errors: string[] } | null }) {
  if (appMode !== "live") return null;
  if (!lastRun) {
    return <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: -8 }}>No run has reported back yet.</div>;
  }
  const at = new Date(lastRun.at);
  const days = (Date.now() - at.getTime()) / 86_400_000;
  const stale = days > 8;
  return (
    <div style={{ fontSize: 12.5, color: stale || lastRun.errors.length ? "var(--warn)" : "var(--muted)", marginTop: -8 }}>
      Last run {at.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
      {" "}{lastRun.source === "auto" ? "automatically" : "from Send to Google"} · {lastRun.placed} placed
      {lastRun.missed ? ` · ${lastRun.missed} found no slot` : ""}
      {lastRun.errors.length ? ` · ${lastRun.errors[0]}` : ""}
      {stale && " · Nothing for over a week: is automatic planning on (Hours & defaults)?"}
    </div>
  );
}

function SetupNote({ token, scriptUrl }: { token: boolean; scriptUrl: boolean }) {
  const missing = [!token && "BLOCKS_TOKEN", !scriptUrl && "BLOCKS_SCRIPT_URL"].filter(Boolean).join(" and ");
  return (
    <div style={{ ...card, padding: "12px 16px", fontSize: 12.5, color: "var(--ink2)", background: "var(--warnTint)" }}>
      <strong>Not connected to Google yet.</strong> The Worker is missing {missing}. The steps are in
      {" "}<span className="mono">docs/work-blocks/README.md</span> in the project-hub repo. You can set up blocks and estimates meanwhile.
    </div>
  );
}

function Figure({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: string }) {
  return (
    <div>
      <div style={{ fontSize: 11.5, color: "var(--muted)" }}>{label}</div>
      <div className="mono" style={{ fontSize: 18, fontWeight: 600, color: tone ?? "var(--ink)", marginTop: 2 }}>{value}</div>
      {hint && <div style={{ fontSize: 11, color: "var(--muted2)", marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

/** Late first, then by the day they are scheduled. */
function sortItems(list: Item[]): Item[] {
  return [...list].sort((a, b) => ((a.start || a.due || "9999").slice(0, 10)).localeCompare((b.start || b.due || "9999").slice(0, 10))
    || a.title.localeCompare(b.title));
}

const sumValues = (m: Map<string, number>): number => [...m.values()].reduce((n, v) => n + v, 0);

const stepBtn: React.CSSProperties = {
  width: 24, height: 24, padding: 0, borderRadius: 6, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", cursor: "pointer", fontSize: 14, lineHeight: 1,
};
